import React, { useState, useEffect } from "react";
import {
  Button,
  Tag,
  Space,
  Drawer,
  Row,
  Col,
  Card,
  Statistic,
  Input,
  Select,
  DatePicker,
  Badge,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  FilterOutlined,
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Icon } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import WorkOrderAside from "./workOrderAside";
import { formatCurrency } from "../../utils/Utils";

const { Option } = Select;
const { RangePicker } = DatePicker;

const WorkOrderAmendment: React.FC = () => {
  const navigate = useNavigate();
  const [sm, setSm] = useState(false);
  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [filters, setFilters] = useState({
    contractType: "",
    organisation: "",
    contractor: "",
    woValueRange: "",
    dateRange: null as any,
    slaStatus: "",
  });

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      "Changes Requested": "processing",
      Pending: "warning",
      Accepted: "success",
      Rejected: "error",
      Finalized: "success",
    };
    return statusColors[status] || "default";
  };

  const handleViewWorkOrder = (record: any) => {
    navigate(`/view-work-order-details/${record.workOrderId}`);
  };

  const resetFilters = () => {
    setFilters({
      contractType: "",
      organisation: "",
      contractor: "",
      woValueRange: "",
      dateRange: null,
      slaStatus: "",
    });
  };

  const applyFilters = () => {
    setDrawerVisible(false);
    // Filter logic would be implemented here
  };

  // KPI calculations
  const totalValue = workOrders.reduce((sum, wo) => sum + wo.woValue, 0);
  const slaBreachCount = workOrders.filter((wo) => wo.slaAge > 2).length;
  const avgSlaAge =
    workOrders.length > 0
      ? Math.round(
          workOrders.reduce((sum, wo) => sum + wo.slaAge, 0) / workOrders.length
        )
      : 0;

  const columns: TableColumnsType<any> = [
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 200,
      render: (text: string, record: any) => (
        <span
          className="inlineTableCellLink"
          id={`woStatus-${record?.workOrderId}`}
          onClick={() => handleViewWorkOrder(record)}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Work Order Title",
      dataIndex: "workOrderTitle",
      key: "workOrderTitle",
      width: 280,
      render: (text, record) => (
        <div>
          <Link
            to={`/view-work-order-details/${record.workOrderId}`}
            className="fw-bold text-primary"
          >
            {text}
          </Link>
          <div className="text-muted small mt-1">
            {record.siteName}
            {record.projectName && ` • ${record.projectName}`}
          </div>
        </div>
      ),
    },
    {
      title: "Organisation",
      dataIndex: "orgName",
      key: "orgName",
      width: 220,
    },
    {
      title: "Contractor",
      dataIndex: "contractorName",
      key: "contractorName",
      width: 200,
    },
    {
      title: "Contract Type",
      dataIndex: "contractType",
      key: "contractType",
      width: 140,
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "WO Value",
      dataIndex: "grossValue",
      key: "grossValue",
      width: 150,
      align: "right",
      render: (value) => <strong>{formatCurrency(value, "INR")}</strong>,
      sorter: (a, b) => a.grossValue - b.grossValue,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: "Date Released",
      dataIndex: "dateReleased",
      key: "dateReleased",
      width: 140,
      render: (text) => dayjs(text).format("DD-MMM-YYYY"),
      sorter: (a, b) =>
        dayjs(a.dateReleased).unix() - dayjs(b.dateReleased).unix(),
    },
    // {
    //   title: "SLA Age",
    //   dataIndex: "slaAge",
    //   key: "slaAge",
    //   width: 100,
    //   align: "center",
    //   render: (days) => (
    //     <Badge
    //       count={`${days}d`}
    //       color={days > 2 ? "#ff4d4f" : days > 1 ? "#faad14" : "#52c41a"}
    //     />
    //   ),
    //   sorter: (a, b) => a.slaAge - b.slaAge,
    // },
    {
      title: "Contract Owner",
      dataIndex: "workOrderOwnerName",
      key: "workOrderOwnerName",
      width: 150,
    },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   fixed: "right",
    //   width: 200,
    //   render: (_, record) => (
    //     <Space wrap>
    //       <Button
    //         type="link"
    //         size="small"
    //         icon={<Icon name="eye" />}
    //         onClick={() => handleViewWorkOrder(record)}
    //       >
    //         View
    //       </Button>
    //       <Button type="link" size="small" icon={<Icon name="clock" />}>
    //         History
    //       </Button>
    //     </Space>
    //   ),
    // },
  ];

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="work-order-amendment"
      title="Work Order Amendment"
      columns={columns}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <WorkOrderAside updateSm={setSm} sm={sm} />
        </div>
      }
      extra={{
        tableScrollX: 1900,
        showSearch: true,
        searchPlaceholder: "Search by WO#, Title, or Organisation...",
        kpiWidgets: [
          {
            title: "Total Under Negotiation",
            value: workOrders.length,
            icon: <ClockCircleOutlined />,
            color: "#faad14",
          },
          {
            title: "Total Value",
            value: formatCurrency(totalValue, "INR"),
            icon: <DollarOutlined />,
            color: "#52c41a",
          },
          {
            title: "SLA Breaches",
            value: slaBreachCount,
            icon: <CalendarOutlined />,
            color: "#ff4d4f",
          },
          {
            title: "Avg SLA Age",
            value: `${avgSlaAge} days`,
            icon: <UserOutlined />,
            color: "#1890ff",
          },
        ],
        advancedFilter: {
          visible: drawerVisible,
          onClose: () => setDrawerVisible(false),
          onOpen: () => setDrawerVisible(true),
          content: (
            <div className="p-4">
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <label className="form-label">Contract Type</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Contract Type"
                    value={filters.contractType}
                    onChange={(value) =>
                      setFilters({ ...filters, contractType: value })
                    }
                  >
                    <Option value="Fixed Price">Fixed Price</Option>
                    <Option value="Time & Material">Time & Material</Option>
                    <Option value="Cost Plus">Cost Plus</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Organisation</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Organisation"
                    value={filters.organisation}
                    onChange={(value) =>
                      setFilters({ ...filters, organisation: value })
                    }
                  >
                    <Option value="ABC Corp">ABC Corp</Option>
                    <Option value="XYZ Ltd">XYZ Ltd</Option>
                    <Option value="DEF Industries">DEF Industries</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Contractor</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Contractor"
                    value={filters.contractor}
                    onChange={(value) =>
                      setFilters({ ...filters, contractor: value })
                    }
                  >
                    <Option value="TechBuild Solutions">
                      TechBuild Solutions
                    </Option>
                    <Option value="InfraMax Ltd">InfraMax Ltd</Option>
                    <Option value="BuildPro Services">BuildPro Services</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">WO Value Range</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Value Range"
                    value={filters.woValueRange}
                    onChange={(value) =>
                      setFilters({ ...filters, woValueRange: value })
                    }
                  >
                    <Option value="0-100000">₹0 - ₹1 Lakh</Option>
                    <Option value="100000-500000">₹1 Lakh - ₹5 Lakh</Option>
                    <Option value="500000-1000000">₹5 Lakh - ₹10 Lakh</Option>
                    <Option value="1000000+">₹10 Lakh+</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Date Range</label>
                  <RangePicker
                    style={{ width: "100%" }}
                    value={filters.dateRange}
                    onChange={(dates) =>
                      setFilters({ ...filters, dateRange: dates })
                    }
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">SLA Status</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select SLA Status"
                    value={filters.slaStatus}
                    onChange={(value) =>
                      setFilters({ ...filters, slaStatus: value })
                    }
                  >
                    <Option value="within">Within SLA</Option>
                    <Option value="breach">SLA Breach</Option>
                  </Select>
                </Col>
              </Row>
              <div className="mt-4 d-flex gap-2">
                <Button
                  type="primary"
                  onClick={applyFilters}
                  style={{ flex: 1 }}
                >
                  Apply Filters
                </Button>
                <Button onClick={resetFilters} style={{ flex: 1 }}>
                  Reset
                </Button>
              </div>
            </div>
          ),
        },
      }}
    />
  );
};

export default WorkOrderAmendment;
